/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import BaseObject from "@shared/BaseObject.js";
import {uuidv4} from "@/utils/helpers";
import {healthConditionColorPickerPalette} from "@/core/js/PaletteEnum";
import GenogramNodeGroupConvexNotchHullRenderer
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramNodeGroupConvexNotchHullRenderer";

const GROUP_COMMAND = "groupmode"
const DEFAULT_GROUP_COLOR = healthConditionColorPickerPalette[0]
const GROUP_STROKE_WIDTH = "3"
const DEFAULT_GROUP_TITLE = ""
export default class GenogramNodeGroupsManager extends BaseObject {

	constructor({
		cy,
		toolbar,
		getGenogramItem,
		requestGroupsChange,
		disableEdgehandles,
		enableEdgehandles,
		disableNodeResize,
		enableNodeResize,
		disableNodeContextMenu,
		enableNodeContextMenu,
		onStateChanged,
		onGroupsChanged,
	}) {
		super()
		this.cy = cy
		this.toolbar = toolbar
		this.getGenogramItem = getGenogramItem || (() => null)
		this.requestGroupsChange = requestGroupsChange || null
		this.disableEdgehandles = disableEdgehandles || (() => {})
		this.enableEdgehandles = enableEdgehandles || (() => {})
		this.disableNodeResize = disableNodeResize || (() => {})
		this.enableNodeResize = enableNodeResize || (() => {})
		this.disableNodeContextMenu = disableNodeContextMenu || (() => {})
		this.enableNodeContextMenu = enableNodeContextMenu || (() => {})
		this.onStateChanged = onStateChanged || (() => {})
		this.onGroupsChanged = onGroupsChanged || (() => {})
		this.isGroupingModeActive = false
		this.isEditingGroups = false
		this.editingGroupId = null
		this.previousSelectionType = null
		this.draftNodeIds = []
		this.lastNonEmptyDraftNodeIds = []
		this.draftGroupColor = DEFAULT_GROUP_COLOR
		this.draftGroupTitle = DEFAULT_GROUP_TITLE
		this.groupHullRenderer = this.createGroupHullRenderer()
		this._listeners = {
			selectionChanged: this.handleSelectionChanged.bind(this),
			coreTap: this.handleCoreTap.bind(this),
		}
		this.cy.on("select unselect", "node", this._listeners.selectionChanged)
		this.cy.on("tap", this._listeners.coreTap)
	}

	createGroupHullRenderer() {
		return new GenogramNodeGroupConvexNotchHullRenderer({
			cy: this.cy,
			getGroups: () => this.getRenderedGroups(),
			getGroupStyle: color => this.getGroupStyle(color),
		})
	}

	startGroupingMode(initialNodeIds = []) {
		if(this.isGroupingModeActive){
			return
		}
		this.isEditingGroups = false
		this.editingGroupId = null
		this.isGroupingModeActive = true
		this.draftNodeIds = initialNodeIds
		this.lastNonEmptyDraftNodeIds = [...initialNodeIds]
		this.draftGroupColor = DEFAULT_GROUP_COLOR
		this.draftGroupTitle = DEFAULT_GROUP_TITLE
		this.previousSelectionType = this.cy.selectionType()
		this.cy.selectionType("additive")
		this.disableEdgehandles()
		this.disableNodeResize()
		this.disableNodeContextMenu()
		this.applyDraftSelection()
		this.syncRenderedGroups()
		this.syncToolbarState()
		this.notifyStateChanged()
	}

	startEditingGroup(groupId) {
		if(!groupId){
			return false
		}
		let group = this.getGroupById(groupId)
		if(!group){
			return false
		}
		let nodeIds = [...new Set(group.nodeIds || [])]
		let groupNodes = this.cy.collection(nodeIds.map(groupNodeId => this.cy.getElementById(groupNodeId)))
			.filter(node => node.isNode() && !node.data("isAuxNode"))
		if(groupNodes.length < 2){
			return false
		}

		this.isEditingGroups = true
		this.editingGroupId = group.id
		this.isGroupingModeActive = true
		this.draftNodeIds = nodeIds
		this.lastNonEmptyDraftNodeIds = [...nodeIds]
		this.draftGroupColor = group.color || DEFAULT_GROUP_COLOR
		this.draftGroupTitle = group.title || DEFAULT_GROUP_TITLE
		this.previousSelectionType = this.cy.selectionType()
		this.cy.selectionType("additive")
		this.disableEdgehandles()
		this.disableNodeResize()
		this.disableNodeContextMenu()
		this.applyDraftSelection()
		this.syncRenderedGroups()
		this.syncToolbarState()
		this.notifyStateChanged()
		return true
	}

	cancelGroupingMode() {
		if(!this.isGroupingModeActive){
			return
		}
		this.isGroupingModeActive = false
		this.isEditingGroups = false
		this.editingGroupId = null
		this.draftNodeIds = []
		this.lastNonEmptyDraftNodeIds = []
		this.draftGroupColor = DEFAULT_GROUP_COLOR
		this.draftGroupTitle = DEFAULT_GROUP_TITLE
		this.cy.elements(":selected").unselect()
		this.restoreSelectionType()
		this.enableEdgehandles()
		this.enableNodeResize()
		this.enableNodeContextMenu()
		this.syncRenderedGroups()
		this.syncToolbarState()
		this.notifyStateChanged()
	}

	saveDraftGroup() {
		let nodeIds = this.getDraftNodeIds()
		if(nodeIds.length < 2){
			return false
		}

		let groups = [...this.getGroups()]
		let title = (this.draftGroupTitle || "").trim()
		if(this.isEditingGroups){
			let editingGroup = this.getGroupById(this.editingGroupId)
			if(!editingGroup){
				return false
			}
			let nextGroup = {
				...editingGroup,
				nodeIds,
				color: this.draftGroupColor || DEFAULT_GROUP_COLOR,
			}
			if(title){
				nextGroup.title = title
			} else {
				delete nextGroup.title
			}
			groups = groups.map(group => group.id === this.editingGroupId ? nextGroup : group)
			this.setGroups(groups)
			this.finishGroupingModeAfterSave()
			return true
		}

		let newGroup = {
			id: uuidv4(),
			nodeIds,
			color: this.draftGroupColor || DEFAULT_GROUP_COLOR,
		}
		if(title){
			newGroup.title = title
		}
		groups.push(newGroup)
		this.setGroups(groups)
		this.finishGroupingModeAfterSave()
		return true
	}

	deleteEditingGroups() {
		if(!this.isEditingGroups || !this.editingGroupId){
			return
		}
		let groups = this.getGroups().filter(group => group.id !== this.editingGroupId)
		this.setGroups(groups)
		this.cancelGroupingMode()
	}

	removeNodeFromAllGroups(nodeId) {
		if(!nodeId){
			return
		}
		let groups = this.removeNodeIdsFromGroups(this.getGroups(), [nodeId])
		this.setGroups(groups)
	}

	deleteGroupsByNodeId(nodeId) {
		if(!nodeId){
			return
		}
		let groups = this.getGroups().filter(group => !group.nodeIds?.includes(nodeId))
		this.setGroups(groups)
	}

	getGroupsForNode(nodeId) {
		if(!nodeId){
			return []
		}
		return this.getGroups().filter(group => group.nodeIds?.includes(nodeId))
	}

	getRenderedGroups() {
		let groups = this.getGroups()
		if(!this.isGroupingModeActive){
			return groups
		}

		let draftGroup = this.createRenderedDraftGroup()
		if(this.isEditingGroups){
			groups = groups.filter(group => group.id !== this.editingGroupId)
		}
		if(!draftGroup){
			return groups
		}
		return [...groups, draftGroup]
	}

	getGroupById(groupId) {
		if(!groupId){
			return null
		}
		return this.getGroups().find(group => group.id === groupId) || null
	}

	reapplyDraftSelection() {
		if(!this.isGroupingModeActive){
			return
		}
		this.applyDraftSelection()
		this.groupHullRenderer?.update()
	}
	setDraftGroupColor(color) {
		if(!color){
			return
		}
		this.draftGroupColor = color
		this.groupHullRenderer?.update()
		this.notifyStateChanged()
	}

	setDraftGroupTitle(title) {
		this.draftGroupTitle = title || DEFAULT_GROUP_TITLE
		this.groupHullRenderer?.update()
		this.notifyStateChanged()
	}

	getDraftNodeIds() {
		return this.cy.nodes(":selected")
			.filter(node => !node.data("isAuxNode"))
			.map(node => node.id())
	}

	syncRenderedGroups() {
		this.cleanupGroupsByExistingNodes()
		this.groupHullRenderer?.update()
	}

	destroy() {
		this.cy.off("select unselect", "node", this._listeners.selectionChanged)
		this.cy.off("tap", this._listeners.coreTap)
		this.groupHullRenderer?.destroy()
		this.groupHullRenderer = null
	}

	finishGroupingModeAfterSave() {
		this.isGroupingModeActive = false
		this.isEditingGroups = false
		this.editingGroupId = null
		this.draftNodeIds = []
		this.lastNonEmptyDraftNodeIds = []
		this.draftGroupColor = DEFAULT_GROUP_COLOR
		this.draftGroupTitle = DEFAULT_GROUP_TITLE
		this.cy.elements(":selected").unselect()
		this.restoreSelectionType()
		this.enableEdgehandles()
		this.enableNodeResize()
		this.enableNodeContextMenu()
		this.syncRenderedGroups()
		this.syncToolbarState()
		this.notifyStateChanged()
	}

	restoreSelectionType() {
		this.cy.selectionType(this.previousSelectionType || "single")
		this.previousSelectionType = null
	}

	syncToolbarState() {
		this.toolbar?.rerender(GROUP_COMMAND, {selected: this.isGroupingModeActive})
	}

	notifyStateChanged() {
		this.onStateChanged({
			isGroupingModeActive: this.isGroupingModeActive,
			isEditingGroups: this.isEditingGroups,
			draftGroupColor: this.draftGroupColor,
			draftGroupTitle: this.draftGroupTitle,
		})
	}

	cleanupGroupsByExistingNodes() {
		let existingNodeIds = new Set(this.cy.nodes().filter(node => !node.data("isAuxNode")).map(node => node.id()))
		let groups = this.getGroups()
		let nextGroups = groups
			.map(group => ({
				...group,
				nodeIds: (group.nodeIds || []).filter(nodeId => existingNodeIds.has(nodeId)),
			}))
			.filter(group => group.nodeIds.length >= 2)
		if(!this.areGroupsEqual(groups, nextGroups)){
			this.setGroups(nextGroups, {syncRenderedGroups: false, useUndoRedo: false})
		}
	}

	areGroupsEqual(groups1 = [], groups2 = []) {
		return JSON.stringify(groups1) === JSON.stringify(groups2)
	}

	removeNodeIdsFromGroups(groups = [], nodeIds = []) {
		let nodeIdSet = new Set(nodeIds)
		return groups
			.map(group => ({
				...group,
				nodeIds: (group.nodeIds || []).filter(nodeId => !nodeIdSet.has(nodeId)),
			}))
			.filter(group => group.nodeIds.length >= 2)
	}

	getGroups() {
		let genogramItem = this.getGenogramItem()
		if(!genogramItem){
			return []
		}
		if(!Array.isArray(genogramItem?.nodeGroups)){
			genogramItem.nodeGroups = []
		}
		return genogramItem.nodeGroups
	}

	setGroups(groups = [], options = {}) {
		let {syncRenderedGroups = true, useUndoRedo = true} = options
		let genogramItem = this.getGenogramItem()
		if(!genogramItem){
			return
		}
		if(useUndoRedo && this.requestGroupsChange){
			this.requestGroupsChange(groups, {syncRenderedGroups})
			return
		}
		genogramItem.nodeGroups = groups
		if(syncRenderedGroups){
			this.syncRenderedGroups()
		}
		this.onGroupsChanged(groups)
	}

	handleSelectionChanged() {
		if(!this.isGroupingModeActive){
			return
		}
		this.draftNodeIds = this.getSelectedNodeIds()
		if(this.draftNodeIds.length > 0){
			this.lastNonEmptyDraftNodeIds = [...this.draftNodeIds]
		}
		this.groupHullRenderer?.update()
	}

	handleCoreTap(event) {
		if(!this.isGroupingModeActive || event.target !== this.cy){
			return
		}
		let draftNodeIds = this.draftNodeIds.length > 0
			? [...this.draftNodeIds]
			: [...this.lastNonEmptyDraftNodeIds]
		setTimeout(() => {
			if(!this.isGroupingModeActive || draftNodeIds.length === 0){
				return
			}
			this.draftNodeIds = draftNodeIds
			this.applyDraftSelection()
		}, 0)
	}

	applyDraftSelection() {
		let draftNodeIds = [...this.draftNodeIds]
		this.cy.elements(":selected").unselect()
		this.cy.collection(draftNodeIds.map(nodeId => this.cy.getElementById(nodeId)))
			.filter(node => node.isNode() && !node.data("isAuxNode"))
			.select()
	}

	createRenderedDraftGroup() {
		let nodeIds = [...new Set(this.draftNodeIds || [])]
		if(nodeIds.length < 1){
			return null
		}

		let draftGroup = {
			id: this.editingGroupId || "__draft_node_group__",
			nodeIds,
			color: this.draftGroupColor || DEFAULT_GROUP_COLOR,
			isDraft: true,
		}
		let title = (this.draftGroupTitle || "").trim()
		if(title){
			draftGroup.title = title
		}
		return draftGroup
	}

	getSelectedNodeIds() {
		return this.cy.nodes(":selected")
			.filter(node => !node.data("isAuxNode"))
			.map(node => node.id())
	}

	getGroupStyle(color = DEFAULT_GROUP_COLOR) {
		return {
			fill: this.toRgba(color, 0.05),
			stroke: this.toRgba(color, 0.55),
			strokeWidth: GROUP_STROKE_WIDTH,
		}
	}

	toRgba(color, alpha) {
		let normalizedColor = (color || DEFAULT_GROUP_COLOR).replace("#", "")
		let fullColor = normalizedColor.length === 3
			? normalizedColor.split("").map(char => `${char}${char}`).join("")
			: normalizedColor
		let red = parseInt(fullColor.slice(0, 2), 16)
		let green = parseInt(fullColor.slice(2, 4), 16)
		let blue = parseInt(fullColor.slice(4, 6), 16)
		return `rgba(${red}, ${green}, ${blue}, ${alpha})`
	}
}

export {
	GROUP_COMMAND,
}
